/**
 * Captures the current viewport to a compressed JPEG blob. `html2canvas` is
 * dynamically imported so it never ships in the initial bundle — it only loads
 * when the user actually opens the feedback flow.
 *
 * Output is downscaled so the longest edge fits within `MAX_EDGE_PX` and
 * re-encoded as JPEG (screenshots compress well enough that PNG isn't worth
 * the bytes). Returns null on failure so callers can fall back to a text-only
 * submission.
 */

import { scaleImageDimensions } from "@/features/website/compressImageForWeb";

const MAX_EDGE_PX = 1600;
const JPEG_QUALITY = 0.78;

export async function captureScreenshot(): Promise<Blob | null> {
  if (typeof document === "undefined") return null;
  try {
    const mod = await import("html2canvas");
    const html2canvas = mod.default;
    const canvas = await html2canvas(document.body, {
      scale: 1,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
      windowWidth: document.documentElement.clientWidth,
      windowHeight: document.documentElement.clientHeight,
    });
    const compressed = downscaleToJpeg(canvas);
    return await compressed;
  } catch {
    return null;
  }
}

async function downscaleToJpeg(source: HTMLCanvasElement): Promise<Blob | null> {
  const { width, height } = scaleImageDimensions(source.width, source.height, MAX_EDGE_PX);
  if (width === source.width && height === source.height) {
    return canvasToBlob(source, "image/jpeg", JPEG_QUALITY);
  }
  const target = document.createElement("canvas");
  target.width = width;
  target.height = height;
  const ctx = target.getContext("2d");
  if (!ctx) return canvasToBlob(source, "image/jpeg", JPEG_QUALITY);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(source, 0, 0, width, height);
  return canvasToBlob(target, "image/jpeg", JPEG_QUALITY);
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), type, quality);
  });
}
